import { useMemo, useState } from "react";

import {
  Star,
  CheckCircle2,
  Clock,
  UserX,
  Users,
  Activity,
  PieChart,
  LineChart,
  BarChart3,
  ListChecks,
  Percent
} from "lucide-react";

import { AlunoDrawer } from "./AlunoDrawer";
import { AlunosTabela } from "./AlunosTabela";
import {
  AreaChartCard,
  LineChartCard,
  NotaBarraLista,
  DonutChart
} from "../../components/charts/Charts";
import { KpiCard } from "../../components/layout/KpiCard";
import {
  Card,
  CardTitle,
  SectionHead,
  Select,
  Skeleton,
  EmptyState,
  EstadoVazio
} from "../../components/ui/primitives";
import { api } from "../../lib/api";
import {
  brPt,
  fmtTempo,
  gradeColor,
  faixaKey,
  FAIXA_META,
  formatPeriodo,
  toNumber
} from "../../lib/format";
import { dentroDoPeriodo, periodoInicial } from "../../lib/periodo";
import {
  separarTiposPorVolume,
  seriesDeTipos,
  linhasPorPeriodo,
  hintDeTipos
} from "../../lib/seriesPorTipo";
import { useAsync } from "../../lib/useAsync";
import { cn } from "../../lib/utils";

export function TurmaPainelView({ turmaId, ano, periodo }) {
  const range = periodo ?? periodoInicial();
  // KPIs de fluxo (nota, taxa, tempo) e desempenho por disciplina seguem o
  // período. A lista de alunos (abaixo) é sempre do ano letivo — ver rótulo.
  const painel = useAsync(
    () => api.turmaPainel(turmaId, undefined, ano, range.inicio, range.fim),
    [turmaId, ano, range.inicio, range.fim]
  );
  const [discId, setDiscId] = useState();
  const alunosPorDisc = useAsync(
    () => api.turmaPainel(turmaId, discId, ano).then(p => p.alunos),
    [turmaId, discId, ano]
  );
  const [aluno, setAluno] = useState(null);

  const distribuicao = useMemo(() => {
    const p = painel.data;
    if (!p) return [];
    const cont = {};
    for (const a of p.alunos) {
      const k = faixaKey(a.faixa);
      cont[k] = (cont[k] ?? 0) + 1;
    }
    const toneColor = {
      acima: "var(--color-good)",
      media: "var(--color-warn)",
      abaixo: "var(--color-bad)",
      sem_referencia: "var(--color-surface-3)"
    };
    return ["acima", "media", "abaixo", "sem_referencia"]
      .filter(k => cont[k])
      .map(k => ({
        name: FAIXA_META[k].label,
        value: cont[k],
        color: toneColor[k]
      }));
  }, [painel.data]);

  if (painel.loading) return <PainelSkeleton />;
  if (painel.error)
    return (
      <EmptyState
        title="Não foi possível carregar a turma"
        hint={painel.error}
      />
    );
  const p = painel.data;

  return (
    <div className="space-y-7">
      {/* Visão geral */}
      <section id="visao-geral" className="scroll-mt-4">
        <SectionHead title="Visão geral" />
        <div className="grid gap-4 lg:grid-cols-[2.4fr_1fr]">
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 sm:gap-4">
            <KpiCard
              label="Nota média da turma"
              value={brPt(p.kpis.nota_media)}
              suffix="/10"
              accent="var(--color-nota)"
              valueColor={gradeColor(p.kpis.nota_media)}
              icon={<Star size={16} />}
            />

            <KpiCard
              label="Total de alunos"
              value={p.meta.total_alunos}
              accent="var(--color-data)"
              sub="matriculados na turma"
              icon={<Users size={16} />}
            />

            <KpiCard
              label="Tempo online"
              value={fmtTempo(p.kpis.tempo_total_seg)}
              accent="var(--color-chart-6)"
              sub="soma do tempo online dos alunos"
              icon={<Clock size={16} />}
            />

            <KpiCard
              label="Taxa de conclusão"
              value={p.kpis.taxa_conclusao ?? "–"}
              suffix="%"
              accent="var(--color-good)"
              sub={`${p.engajamento.concluidas} de ${p.engajamento.atribuidas} atribuições`}
              icon={<CheckCircle2 size={16} />}
            />

            <KpiCard
              label={`Alunos inativos (+${p.kpis.inativos_dias ?? 7} dias)`}
              value={p.kpis.alunos_inativos}
              accent="var(--color-bad)"
              sub="sem acessar a plataforma"
              icon={<UserX size={16} />}
            />

            {/* KPI "Nunca acessaram" removido a pedido do Kilson em 04/08/2026 —
                não quer o card por enquanto, pode voltar a usar depois. O
                backend continua calculando p.kpis.alunos_nunca_acessaram. */}

            <KpiCard
              label="Score de engajamento"
              value={p.kpis.score_engajamento_medio ?? "–"}
              suffix={p.kpis.score_engajamento_medio != null ? "%" : undefined}
              accent="var(--color-chart-5)"
              sub="média da turma no período"
              icon={<Activity size={16} />}
            />
          </div>
          <Card>
            <CardTitle>Distribuição por faixa</CardTitle>
            {distribuicao.length ? (
              <DonutChart data={distribuicao} />
            ) : (
              <EstadoVazio
                icon={<PieChart size={20} />}
                titulo="Sem dados de faixa"
                hint="Nenhum aluno com nota registrada no período."
                altura={190}
              />
            )}
          </Card>
        </div>
      </section>

      {/* Desempenho & engajamento */}
      <section id="desempenho-engajamento" className="scroll-mt-4">
        <SectionHead
          title="Desempenho & engajamento"
          sub="nota ao longo do tempo e execução de atividades"
        />
        <div className="grid gap-4 lg:grid-cols-2">
          <Card>
            <CardTitle meta="Média da turma por mês">
              Evolução da nota média
            </CardTitle>
            {p.evolucao.length ? (
              <AreaChartCard
                data={p.evolucao
                  .filter(r => dentroDoPeriodo(r.periodo, range))
                  .map(r => ({
                    ...r,
                    periodo_fmt: formatPeriodo(r.periodo, "mes")
                  }))}
                xKey="periodo_fmt"
                series={[
                  {
                    key: "nota_media",
                    label: "Nota média",
                    color: "var(--color-data)"
                  }
                ]}
                yDomain={[0, 10]}
              />
            ) : (
              <EstadoVazio
                icon={<LineChart size={20} />}
                titulo="Sem dados no período"
                hint="Nenhuma avaliação registrada nos meses selecionados."
                altura={220}
              />
            )}
          </Card>
          <ProgressoCard progresso={p.progresso} />
          <ExecucaoCard turmaId={turmaId} ano={ano} range={range} />
          <EngajamentoCard turmaId={turmaId} ano={ano} range={range} />
        </div>
      </section>

      {/* Desempenho por disciplina */}
      <section id="desempenho-disciplina" className="scroll-mt-4">
        <SectionHead title="Desempenho por disciplina" />
        <Card>
          {p.por_disciplina.length ? (
            <NotaBarraLista
              data={p.por_disciplina.map(d => ({
                nome: d.disciplina,
                nota: d.nota_media
              }))}
              labelKey="nome"
              valueKey="nota"
              labelWidth={160}
            />
          ) : (
            <EstadoVazio
              icon={<BarChart3 size={20} />}
              titulo="Sem dados no período"
              hint="Nenhuma disciplina com nota registrada no período."
              altura={220}
            />
          )}
        </Card>
      </section>

      {/* Acompanhamento dos alunos */}
      <section id="acompanhamento-alunos" className="scroll-mt-4">
        <SectionHead
          title="Acompanhamento dos alunos"
          sub="notas e faixas referentes ao ano letivo — não mudam com o período selecionado"
        />

        <Card>
          <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
            <span className="text-[11.5px] text-[var(--color-ink-faint)]">
              Clique no cabeçalho para ordenar · clique numa linha para ver a
              evolução
            </span>
            <div className="flex items-center gap-2">
              <span className="text-[12px] text-[var(--color-ink-faint)]">
                Disciplina:
              </span>
              <Select
                value={discId ?? ""}
                onChange={e =>
                  setDiscId(e.target.value ? Number(e.target.value) : undefined)
                }
                aria-label="Filtrar por disciplina"
              >
                <option value="">Todas as disciplinas</option>
                {p.por_disciplina.map(d => (
                  <option key={d.disciplina_id} value={d.disciplina_id}>
                    {d.disciplina}
                  </option>
                ))}
              </Select>
            </div>
          </div>
          {alunosPorDisc.loading ? (
            <Skeleton className="h-[220px] w-full" />
          ) : (
            <AlunosTabela
              alunos={alunosPorDisc.data ?? []}
              onAbrirAluno={setAluno}
              disciplinaId={discId}
            />
          )}
        </Card>
      </section>

      {aluno && (
        <AlunoDrawer
          aluno={aluno}
          turmaId={turmaId}
          ano={ano}
          range={range}
          onClose={() => setAluno(null)}
        />
      )}
    </div>
  );
}

function ProgressoCard({ progresso }) {
  const total = progresso.atividades || 0;
  const iniciadas = progresso.iniciadas || 0;
  const concluidas = progresso.concluidas || 0;
  const naoIniciadas = Math.max(0, total - iniciadas);
  const pctMedio = progresso.pct_medio_conclusao ?? 0;
  const pctTone =
    pctMedio >= 70
      ? "var(--color-good)"
      : pctMedio >= 40
      ? "var(--color-warn)"
      : "var(--color-bad)";

  return (
    <Card>
      <CardTitle meta="Por atividade distinta atribuída à turma">
        Progresso das atividades
      </CardTitle>
      <div className="grid gap-3 sm:grid-cols-2">
        <ResumoBox
          value={`${pctMedio.toFixed(1)}%`}
          label="conclusão média por atividade"
          color={pctTone}
        />
        <ResumoBox
          value={naoIniciadas}
          label={`atividade${
            naoIniciadas === 1 ? "" : "s"
          } que nenhum aluno iniciou`}
          color={naoIniciadas > 0 ? "var(--color-warn)" : "var(--color-good)"}
        />
      </div>
      {/* Funil, não categorias: as três barras são estágios do MESMO fluxo, e
          cada uma é subconjunto da anterior. Por isso uma rampa sequencial de um
          só hue (claro -> escuro conforme avança), em vez de três cores
          diferentes — antes eram neutro/navy/verde, que liam como três coisas
          sem relação. "Atribuídas" fica no neutro por ser a linha de base
          (sempre 100%). O verde saiu de propósito: cor de status é reservada
          para estado, e aqui ela competia com os chips de faixa. */}
      <div className="mt-4 space-y-3">
        <ProgressRow
          label="Atribuídas"
          value={total}
          max={total}
          color="var(--color-ink-faint)"
        />
        <ProgressRow
          label="Iniciadas"
          value={iniciadas}
          max={total}
          color="var(--color-data)"
        />
        <ProgressRow
          label="Concluídas"
          value={concluidas}
          max={total}
          color="var(--color-data-dark)"
        />
      </div>
      <div className="mt-4 text-[11.5px] leading-relaxed text-[var(--color-ink-faint)]">
        {total} atividade{total === 1 ? "" : "s"} distinta
        {total === 1 ? "" : "s"} atribuída{total === 1 ? "" : "s"} à turma ·
        barras = atividades em que ao menos um aluno iniciou/concluiu
      </div>
    </Card>
  );
}

export function ExecucaoCard({ turmaId, ano, range }) {
  const [metrica, setMetrica] = useState("volume");
  const gran = range.gran;
  const exec = useAsync(
    () => api.turmaExecucao(turmaId, gran, ano, range.inicio, range.fim),
    [turmaId, gran, ano, range.inicio, range.fim]
  );

  const chart = useMemo(() => {
    const dados = exec.data;
    if (!dados)
      return { data: [], series: [], hint: "Iniciadas vs. concluídas" };

    if (metrica === "tipo") {
      /* O número de séries aqui vem do DADO, não do código — é o único gráfico
         do painel em que isso acontece, e é onde a paleta pode acabar. A regra
         (quatro cores próprias + "Outros") mora em lib/seriesPorTipo.js, com
         teste. */
      const { tipos, agrupados } = separarTiposPorVolume(dados.por_tipo);
      return {
        data: linhasPorPeriodo(dados.por_tipo, tipos, agrupados, periodo =>
          formatPeriodo(periodo, gran)
        ),
        series: seriesDeTipos(tipos),
        hint: hintDeTipos(agrupados)
      };
    }

    const data = dados.serie.map(r => ({
      ...r,
      periodo_fmt: formatPeriodo(r.periodo, gran)
    }));
    if (metrica === "conclusao") {
      return {
        data,
        series: [
          {
            key: "taxa_conclusao",
            label: "% conclusão",
            color: "var(--color-data)"
          }
        ],
        hint: "% médio de conclusão no período"
      };
    }
    return {
      data,
      /* Mesmas duas séries, mesmas duas cores que no drawer do aluno
         (AlunoDrawer): antes eram âmbar/verde aqui e teal/azul lá — as mesmas
         métricas mudavam de cor conforme a tela, e o professor tinha de
         reaprender a legenda a cada painel. */
      series: [
        { key: "iniciadas", label: "Iniciadas", color: "var(--color-chart-1)" },
        {
          key: "concluidas",
          label: "Concluídas",
          color: "var(--color-chart-3)"
        }
      ],

      hint: "Iniciadas (data de início) vs. concluídas (data de conclusão)"
    };
  }, [exec.data, gran, metrica]);

  return (
    <Card className="relative">
      <div className="mb-3 pr-32">
        <CardTitle meta={chart.hint}>Execução de atividades</CardTitle>
      </div>
      <div className="absolute right-4 top-4 flex items-center gap-3 rounded-lg border border-[var(--color-border)] bg-[var(--color-surface-2)]/40 p-1 sm:right-5 sm:top-5">
        {[
          ["volume", "Volume", BarChart3],
          ["conclusao", "Taxa", Percent],
          ["tipo", "Por tipo", PieChart]
        ].map(([v, label, Icon]) => (
          <button
            key={v}
            type="button"
            title={label}
            aria-label={label}
            aria-pressed={metrica === v}
            onClick={() => setMetrica(v)}
            className={cn(
              "grid h-7 w-7 place-items-center rounded-md transition-all",
              metrica === v
                ? "bg-[var(--color-surface)] text-[var(--color-brand)] shadow-sm"
                : "text-[var(--color-ink-faint)] hover:text-[var(--color-ink-soft)]"
            )}
          >
            <Icon size={14} />
          </button>
        ))}
      </div>
      {exec.loading ? (
        <Skeleton className="h-[240px] w-full" />
      ) : chart.data.length ? (
        <LineChartCard
          data={chart.data}
          xKey="periodo_fmt"
          series={chart.series}
          height={250}
          yDomain={metrica === "conclusao" ? [0, 100] : undefined}
          yTickFormatter={metrica === "conclusao" ? v => `${v}%` : undefined}
        />
      ) : (
        <EstadoVazio
          icon={<ListChecks size={20} />}
          titulo="Sem execução no período"
          hint="Nenhuma atividade atribuída ou concluída na janela selecionada."
          altura={220}
        />
      )}
    </Card>
  );
}

export function EngajamentoCard({ turmaId, ano, range }) {
  const gran = range.gran;
  const eng = useAsync(
    () => api.turmaEngajamento(turmaId, gran, ano, range.inicio, range.fim),
    [turmaId, gran, ano, range.inicio, range.fim]
  );
  const serie = useMemo(() => {
    const pts = eng.data ?? [];
    const porPeriodo = {};
    for (const p of pts) {
      const score = toNumber(p.score);
      if (score === null) continue;
      // sem atribuição encadeada (no-multi-assign do airbnb)
      if (!porPeriodo[p.periodo]) porPeriodo[p.periodo] = { soma: 0, n: 0 };
      const b = porPeriodo[p.periodo];
      b.soma += score;
      b.n += 1;
    }
    return Object.entries(porPeriodo)
      .filter(([, v]) => v.n > 0)
      .sort(([a], [b]) => a.localeCompare(b))
      .map(([periodo, v]) => ({
        periodo_fmt: formatPeriodo(periodo, gran),
        score: Math.round(v.soma / v.n)
      }));
  }, [eng.data, gran]);

  return (
    <Card>
      <div className="mb-3 flex flex-wrap items-start justify-between gap-3">
        <CardTitle
          meta={`Score final médio da turma — ${serie.length} períodos`}
        >
          Engajamento da turma
        </CardTitle>
      </div>
      {eng.loading ? (
        <Skeleton className="h-[240px] w-full" />
      ) : serie.length ? (
        <AreaChartCard
          data={serie}
          xKey="periodo_fmt"
          series={[
            { key: "score", label: "Score final", color: "var(--color-data)" }
          ]}
          height={250}
          yDomain={[0, 100]}
          yTickFormatter={v => `${v}%`}
          xTickAngle={-25}
          xInterval={gran === "dia" ? "preserveStartEnd" : 0}
        />
      ) : (
        <EstadoVazio
          icon={<Activity size={20} />}
          titulo="Sem dados de engajamento"
          hint="Nenhum score registrado para a turma no período."
          altura={220}
        />
      )}
    </Card>
  );
}

function ResumoBox({ value, label, color }) {
  return (
    <div className="rounded-2xl border border-[var(--color-border)] bg-[var(--color-surface-2)] px-4 py-3">
      <div
        className="tnum text-[24px] font-bold leading-none"
        style={{ color }}
      >
        {value}
      </div>
      <div className="mt-1.5 text-[11.5px] leading-snug text-[var(--color-ink-soft)]">
        {label}
      </div>
    </div>
  );
}

/* Barra do card "Progresso das atividades".

   O número mora DENTRO da barra quando ela é larga o bastante, e FORA, à
   direita dela, quando não é. Sem isso, valor pequeno — e principalmente zero —
   colava o número na borda esquerda do card: a barra ficava sem largura, mas o
   `px-2` a mantinha com 16px de caixa, e o número aparecia grudado ali, em
   branco sobre um toco de cor. Relato do Kilson em 05/08/2026, na turma sem
   atividades (0 atribuídas, 0 iniciadas, 0 concluídas).

   Duas consequências disso, e as duas importam:
     - com `dentro` falso o padding também sai, senão a barra de valor zero
       continuaria ocupando os 16px do padding e desenhando um toco colorido
       para representar "nada";
     - o número fora da barra troca o branco pela cor de texto, porque ali o
       fundo é a trilha clara, não a cor cheia. */
function ProgressRow({ label, value, max, color }) {
  const pct =
    max > 0 && value > 0 ? Math.max(2, Math.min(100, (value / max) * 100)) : 0;
  // 14% é onde dois dígitos ainda cabem na barra sem encostar nas bordas.
  const dentro = pct >= 14;
  return (
    <div className="grid grid-cols-[86px_1fr] items-center gap-3">
      <div className="text-[13px] text-[var(--color-ink-soft)]">{label}</div>
      <div className="flex h-7 items-center overflow-hidden rounded-lg bg-[var(--color-surface-3)]">
        <div
          className={cn(
            "flex h-full flex-none items-center justify-end rounded-lg text-[12px] font-bold text-white transition-[width] duration-700",
            dentro && "px-2"
          )}
          style={{ width: `${pct}%`, background: color }}
        >
          {dentro && value}
        </div>
        {!dentro && (
          <span className="tnum flex-none pl-2.5 text-[12px] font-bold text-[var(--color-ink-soft)]">
            {value}
          </span>
        )}
      </div>
    </div>
  );
}

function PainelSkeleton() {
  return (
    <div className="space-y-7">
      <div className="grid gap-4 lg:grid-cols-[2.4fr_1fr]">
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 sm:gap-4">
          {Array.from({ length: 6 }).map((_, i) => (
            <Skeleton key={i} className="h-[104px]" />
          ))}
        </div>
        <Skeleton className="h-[104px] lg:h-auto" />
      </div>
      <Skeleton className="h-[280px]" />
      <Skeleton className="h-[320px]" />
    </div>
  );
}
